import {
  OpenPanelOptions,
  useAiSlidePanel,
} from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { memo, useLayoutEffect } from "react";
import {
  AskRedoPanelOptions,
  useAskRedoSlidePanel,
} from "./ask-redo-slide-panel-context";

export function translateLegacyPanelOptions(
  options: OpenPanelOptions | null,
): AskRedoPanelOptions | undefined {
  if (options?.directEscalation) {
    const {
      escalationId,
      intercomConversationId,
      noticeTopic,
      triageThreadId,
    } = options.directEscalation;
    return {
      kind: "directEscalation",
      escalationId,
      intercomConversationId,
      noticeTopic,
      triageThreadId,
    };
  }
  if (options?.prefillInput) {
    return {
      kind: "prefill",
      input: options.prefillInput,
      autoSubmit: options.autoSubmit,
    };
  }
  return undefined;
}

/**
 * Forwards legacy `useAiSlidePanel().open()` requests into the Ask
 * Redo slide-out. Mounted only while the ASK_REDO_UI flag is on (see
 * `AskRedoLayout`).
 *
 * Why: the two panels keep deliberately separate state, and the
 * migration strategy was to branch on the flag at every "open the AI
 * chat" call site. Call sites that weren't branched — e.g. the OMS
 * automations page's "Create with AI" (OMS-3245), parcels/packing
 * slips "Manage with AI", billing cards, the return-page AI review —
 * still open the LEGACY context. With the flag on, `GlobalAI` renders
 * null, so the legacy `AiSlidePanelLayout` expanded an empty dark
 * column ("black box") with no close button: the Ask Redo panel then
 * shoved it further left, and the dead column persisted across
 * navigation until a refresh.
 *
 * Instead of chasing every call site (and every future one), this
 * bridge redirects: when the legacy context reports open, translate
 * its options to `AskRedoPanelOptions`, open the Ask Redo panel, and
 * close the legacy context again. `useLayoutEffect` so the legacy
 * column never paints. This is the ONE deliberate entanglement
 * between the two systems — delete it together with the legacy panel
 * once Ask Redo is fully rolled out.
 */
export const LegacyAiPanelBridge = memo(function LegacyAiPanelBridge() {
  const {
    isOpen: legacyOpen,
    panelOptions: legacyOptions,
    close: closeLegacy,
  } = useAiSlidePanel();
  const { open: openAskRedo } = useAskRedoSlidePanel();

  useLayoutEffect(() => {
    if (!legacyOpen) {
      return;
    }
    openAskRedo(translateLegacyPanelOptions(legacyOptions));
    // Closing also clears the legacy panelOptions, so this effect's
    // re-run (isOpen now false) is a no-op rather than a loop.
    closeLegacy();
  }, [legacyOpen, legacyOptions, openAskRedo, closeLegacy]);

  return null;
});
